import { isAppLut } from 'legacy/lut/functions'
import { memo, useCallback, useMemo, useState } from 'react'

import { IPartner, IPartnerFlags } from 'clickNgo/entities/partner/models'
import { PartnerEditor } from 'clickNgo/entities/partner/ui/partnerEditor'
import { IAttachment } from 'clickNgo/models/file'
import { useSafetyTranslation } from 'cng-common/features/translations'
import { MY_CONNECT_TYPE } from 'dialer/classes/models/MyConnectType'
import {
  CALL_MODE,
  CallablePhoneWrapper,
  getFullPhone,
} from 'dialer/components/widgets/public/callPhoneWrapper/CallablePhoneWrapper'

import { CockpitIconButton } from '../../../../../components/cockpitIconButton'
import { HandlerProps } from '../../models'
import { companyDetailsTranslations } from '../../translation'

import { getTelLink, getUrl } from './helpers'

import styles from './CompanyDetailsActions.module.scss'

type Props = {
  partner: IPartner
  partnerFlags: IPartnerFlags
  partnerAttachments: IAttachment[]
} & HandlerProps

/** The partner's website, phone and settings, as the chips a tile header carries. Website and
 *  phone are links; without an address or a number their chip stands disabled. */
export const CompanyDetailsActions = memo<Props>(
  ({
    partner,
    partnerFlags,
    partnerAttachments,
    onPartnerUpdate,
    onPartnerFlagsUpdate,
    onPartnerAttachmentsUpdate,
    isReadOnly,
  }) => {
    const t = useSafetyTranslation()
    const [isEditPartnerDialogVisible, setIsEditPartnerDialogVisible] = useState(false)

    const partnerUrl = getUrl(partner.url)
    const partnerPhone = useMemo(
      () => getFullPhone(partner.phoneCountryCode, partner.phone) || partner.phone,
      [partner.phone, partner.phoneCountryCode]
    )
    const partnerTel = useMemo(
      () => getTelLink({ guid: partner.hpdPartnerGuid, phone: partnerPhone }),
      [partner.hpdPartnerGuid, partnerPhone]
    )
    const isPhoneActionActive = Boolean(partnerPhone)

    const onEditPartnerDialogOpenHandler = useCallback(() => {
      setIsEditPartnerDialogVisible(true)
    }, [])

    const onEditPartnerDialogCloseHandler = useCallback(() => {
      setIsEditPartnerDialogVisible(false)
    }, [])

    return (
      <div className={styles.container}>
        <CockpitIconButton
          icon="hpicon hpicon-social-website"
          label={t(companyDetailsTranslations.actions.website)}
          href={partnerUrl}
          target="_blank"
          disabled={!partnerUrl}
        />
        {isPhoneActionActive ? (
          <CallablePhoneWrapper
            className={styles.phoneActionWrapper}
            callMode={CALL_MODE.byPhoneClick}
            phone={partnerPhone}
            type={MY_CONNECT_TYPE.Partner}
            additionalAttributes={{
              partnerId: partner.partnerId,
              contactProcessedInScout: isAppLut(),
            }}
          >
            <CockpitIconButton
              icon="hpicon hpicon-telephone2"
              label={t(companyDetailsTranslations.actions.phoneCall)}
              href={partnerTel}
            />
          </CallablePhoneWrapper>
        ) : (
          <CockpitIconButton
            icon="hpicon hpicon-telephone2"
            label={t(companyDetailsTranslations.actions.phoneCall)}
            disabled
          />
        )}
        <CockpitIconButton
          icon="kbase kbase-settings"
          label={t('jbp-partner-details-cog')}
          onClick={onEditPartnerDialogOpenHandler}
        />
        <PartnerEditor
          isVisible={isEditPartnerDialogVisible}
          isReadOnly={isReadOnly}
          partner={partner}
          partnerFlags={partnerFlags}
          partnerAttachments={partnerAttachments}
          onClose={onEditPartnerDialogCloseHandler}
          onPartnerUpdate={onPartnerUpdate}
          onPartnerFlagsUpdate={onPartnerFlagsUpdate}
          onPartnerAttachmentsUpdate={onPartnerAttachmentsUpdate}
        />
      </div>
    )
  }
)

CompanyDetailsActions.displayName = 'CompanyDetailsActions'
